import { useState, useEffect } from 'react';

export default function ArticleDetail({ articleId }) {
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Fetch article from Directus API
    const fetchArticle = async () => {
      try {
        setLoading(true);
        
        // Replace with your actual Directus API endpoint
        const response = await fetch(`https://cms.lizakhan.com/items/articles/${articleId}`);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch: ${response.status}`);
        }
        
        const data = await response.json();
        setArticle(data.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching article:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    if (articleId) {
      fetchArticle();
    }
  }, [articleId]);

  if (loading) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorState message={error} />;
  }

  if (!article) {
    return <NotFoundState />;
  }

  return (
    <div className="container py-4">
      <ArticleContent article={article} />
    </div>
  );
}

// Article Content Component
function ArticleContent({ article }) {
  const { title, lead, leadImage, body, bodyImage } = article;
  
  // Get the asset URL for images (adjust based on your Directus setup)
  const leadImageUrl = leadImage ? `https://cms.lizakhan.com/assets/${leadImage}` : null;
  const bodyImageUrl = bodyImage ? `https://cms.lizakhan.com/assets/${bodyImage}` : null;
  
  // Function to render body content - this assumes body content is HTML
  // If your body content is in a different format (like Markdown), you'll need a parser
  const renderBodyContent = () => {
    if (!body) return null;
    
    // Option 1: If body is HTML and you want to render it
    return <div dangerouslySetInnerHTML={{ __html: body }} />;
    
    // Option 2: If body is plain text, use this instead:
    // return <div>{body.split('\n').map((paragraph, i) => <p key={i}>{paragraph}</p>)}</div>;
  };

  return (
    <article>
      <div className="mb-4">
        <button onClick={() => window.history.back()} className="btn btn-outline-secondary btn-sm mb-3">
          &larr; Back to Articles
        </button>
        <h1 className="display-5 fw-bold">{title}</h1>
        {lead && <p className="lead">{lead}</p>}
      </div>
      
      {leadImageUrl && (
        <div className="mb-4">
          <img 
            src={leadImageUrl} 
            alt={title}
            className="img-fluid rounded w-100"
          />
        </div>
      )}
      
      <div className="row">
        <div className="col-lg-8">
          <div className="article-body mb-4">
            {renderBodyContent()}
            
            {/* Body image can appear here or within the body content depending on your setup */}
            {bodyImageUrl && (
              <div className="my-4">
                <img 
                  src={bodyImageUrl} 
                  alt="Article visual"
                  className="img-fluid rounded"
                />
              </div>
            )}
          </div>
        </div>
        
        <div className="col-lg-4">
          {/* Optional sidebar for related content, metadata, etc. */}
          <div className="card bg-light">
            <div className="card-body">
              <h5 className="card-title">About this article</h5>
              <p className="card-text">Published on {formatDate(article.date_created || '')}</p>
              
              {/* Add additional metadata here if available */}
              {article.author && (
                <p className="card-text">Author: {article.author}</p>
              )}
              
              {article.categories && article.categories.length > 0 && (
                <div className="mt-3">
                  <h6>Categories</h6>
                  <div>
                    {article.categories.map(category => (
                      <span key={category} className="badge bg-secondary me-1">
                        {category}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}

// Helper function to format dates
function formatDate(dateString) {
  if (!dateString) return 'Unknown date';
  
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }).format(date);
}

// Loading State Component
function LoadingState() {
  return (
    <div className="container py-4">
      <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-secondary">Loading article...</p>
        </div>
      </div>
    </div>
  );
}

// Error State Component
function ErrorState({ message }) {
  return (
    <div className="container py-4">
      <div className="alert alert-danger text-center" role="alert">
        <h4 className="alert-heading">Unable to load article</h4>
        <p>{message}</p>
        <hr />
        <p className="mb-0">
          <button 
            onClick={() => window.history.back()} 
            className="btn btn-outline-danger btn-sm"
          >
            Return to articles
          </button>
        </p>
      </div>
    </div>
  );
}

// Not Found State Component
function NotFoundState() {
  return (
    <div className="container py-4">
      <div className="alert alert-warning text-center" role="alert">
        <h4 className="alert-heading">Article Not Found</h4>
        <p>The article you're looking for doesn't exist or has been removed.</p>
        <hr />
        <p className="mb-0">
          <button 
            onClick={() => window.history.back()} 
            className="btn btn-outline-warning btn-sm"
          >
            Return to articles
          </button>
        </p>
      </div>
    </div>
  );
}
